// Stale or not, Success will *always* have data

/**
 * Produce a loading state. Forwards the current `data` if present and sets the stale flag accordingly.
 *
 * @example
 * ```ts
 *
 * const data = {
 *   data: 'foo',
 *   error: null,
 *   loading: false,
 *   stale: false
 * };
 *
 * toLoading()(data) = {
 *   data: 'foo', // Copies data
 *   error: null,
 *   loading: true, // Sets loading
 *   stale: true // Sets stale: true
 * }
 *
 * // Curried for use in conjunction with `withFunctionalNext` to automatically derive the correct state:
 *
 * runtime.next(toLoading())
 * ```
 */
export const toLoading = () => state => state !== null && state !== void 0 && state.data ? {
  data: state.data,
  error: null,
  loading: true,
  stale: true
} : {
  data: undefined,
  error: null,
  loading: true,
  stale: false
};

/**
 * Produce a success state. Does not require current state, but curried the same for consistency.
 *
 * @example
 * ```ts
 *
 * const data = { id: '1' };
 *
 * toSuccess(data)() = {
 *   data: { id: '1' },
 *   error: null,
 *   loading: false,
 *   stale: false
 * }
 *
 * // Curried for use in conjunction with `withFunctionalNext` to automatically derive the correct state:
 *
 * runtime.next(toSuccess(data))
 * ```
 */
export const toSuccess = nextData => () => ({
  data: nextData,
  error: null,
  loading: false,
  stale: false
});

/**
 * Produce an error state. Forwards the current `data` if present and sets the stale flag accordingly.
 *
 * @example
 * ```ts
 *
 * const state = {
 *   data: { id: '1' },
 *   error: null,
 *   loading: false,
 *   stale: false
 * };
 *
 * toError(new Error('Failed to fetch'))(state) = {
 *   data: { id: '1' },
 *   error: Error('Failed to fetch'),
 *   loading: false,
 *   stale: true
 * }
 *
 * // Curried for use in conjunction with `withFunctionalNext` to automatically derive the correct state:
 *
 * runtime.next(toError(new Error('Failed to fetch')))
 */
export const toError = error => state => {
  if (!(state !== null && state !== void 0 && state.data)) {
    return {
      data: undefined,
      error,
      loading: false,
      stale: false
    };
  }
  return {
    data: state.data,
    error,
    loading: false,
    stale: true
  };
};

/**
 * Mark a state as stale if data is present. If no data, will no-op.
 *
 * No-ops for any state without data — with no data present, stale has no meaning
 *
 * @example
 * ```ts
 *
 * const state = {
 *   data: { id: '1' },
 *   error: null,
 *   loading: false,
 *   stale: false
 * };
 *
 * markStale()(state) = {
 *   data: { id: '1' },
 *   error: null,
 *   loading: false,
 *   stale: true
 * }
 *
 * const noDataState = {
 *   error: null,
 *   loading: true,
 *   stale: false
 * };
 *
 * markStale()(noDataState) = {
 *   error: null,
 *   loading: true,
 *   stale: false
 * };
 * ```
 *
 * // Curried for use in conjunction with `withFunctionalNext` to automatically derive the correct state:
 *
 * runtime.next(markStale())
 */
export const markStale = () => state => {
  if (!state.data || state.stale) {
    return state; // reuse reference
  }
  return Object.assign({}, state, {
    stale: true
  });
};